<template>
  <component
    :is="href ? NuxtLink : 'button'"
    :to="isDownload ? href : localePath(href)"
    :target="(isDownload ? '_blank' : target) || '_self'"
    :download="shouldForceDownload ? '' : undefined"
    :style="{
      backgroundColor: bgColor,
      borderColor: '#000000',
      borderBottomColor: overrideBorderBottomColor,
      borderBottomWidth: overrideBorderBottomColor ? '1px' : undefined,
      color: bgColor === '#000000' ? '#FFFFFF' : '#000000',
    }"
    :class="[
      'group flex w-full cursor-pointer items-center justify-between border border-b-0 px-3.75 py-2.5 last:border-b',
    ]"
    :aria-label="ariaLabel"
  >
    <div class="flex w-full items-center justify-between">
      <span
        class="max-w-[90%] text-start text-base leading-[120%] transition-transform duration-100 ease-in-out group-hover:translate-x-1 md:text-[17px] xl:text-xl 2xl:text-[23px]"
      >
        {{ title }}
      </span>

      <svg
        viewBox="0 0 26 26"
        class="block h-6.5 w-6.5 shrink-0"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="13" cy="13" r="13" :fill="circleColor" />

        <path
          v-if="isDownload"
          d="M13 6.5v8.5M7.5 10 13 15.5 18.5 10M7 19.5h12"
          :stroke="arrowColor"
          stroke-width="2"
          fill="none"
          stroke-linecap="butt"
          stroke-linejoin="miter"
        />

        <path
          v-else
          d="M7 13h11M13 7.5 18.5 13 13 18.5"
          :stroke="arrowColor"
          stroke-width="2"
          fill="none"
          stroke-linecap="butt"
          stroke-linejoin="miter"
        />
      </svg>
    </div>
  </component>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

const NuxtLink = resolveComponent('NuxtLink')
const localePath = useLocalePath()

interface Props {
  href?: RouteLocationRaw
  title: string
  color?: string
  pageColor?: string
  ariaLabel?: string
  target?: '_blank' | '_self' | '_parent' | '_top'
  isDownload?: boolean
  overrideBorderBottomColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  href: '',
  color: 'black',
  ariaLabel: 'button',
  isDownload: false,
})

const isPdf = computed(() => {
  const hrefStr = typeof props.href === 'string' ? props.href.toLowerCase() : ''
  return hrefStr.includes('.pdf')
})

const shouldForceDownload = computed(() => {
  return props.isDownload && !isPdf.value
})

const resolveButtonColor = computed(() => {
  const c = props.color.toLowerCase()

  switch (c) {
    case 'black':
      return '#000000'
    case 'white':
    case '#ffffff':
      return '#FFFFFF'
    case 'grey':
      return '#E0E0E0'
    case 'ffd900':
      return '#FFD900'
    default:
      return c.startsWith('#') ? c : '#000000'
  }
})

const bgColor = computed(() => resolveButtonColor.value)

const circleColor = computed(() => {
  return bgColor.value === '#000000' ? '#FFFFFF' : '#000000'
})

const arrowColor = computed(() => {
  if (props.pageColor) {
    return props.pageColor
  }
  return bgColor.value
})
</script>
